import { CommonModule } from '@angular/common';
import { MaskService } from '../../../../../shared/services/mask.service';
import { UtilService } from '../../../../../shared/services/util.service';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TipoParcelamento } from '../../../../../shared/services/models/financas/tipo-parcelamento.model';
import { FrequenciaRecorrencia } from '../../../../../shared/services/models/financas/frequencia-recorrencia.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { FormaRecebimento } from '../../../../../shared/services/models/financas/forma-recebimento.model';
import { ContasReceberService } from '../../../../../shared/services/apis/financas/contas-receber.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CampoObrigatorioStruct } from '../../../../../shared/services/structs/financas/campo-obrigatorio.struct';
import { CodigoSefazEnum } from '../../../../../shared/enums/financas/codigo-sefaz.enum';
import { TipoContaEnum } from '../../../../../shared/enums/financas/tipo-conta.enum';
import { StatusContasReceberEnum } from '../../../../../shared/enums/financas/status-contas-receber.enum';
import { FrequenciaRecorrenciaEnum } from '../../../../../shared/enums/financas/frequencia-recorrencia.enum';
import { AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-contas-receber-parcelamento',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    SelectPesquisavelComponent,
    AbntPipe
  ],
  templateUrl: './contas-receber-parcelamento.component.html',
  styleUrl: './contas-receber-parcelamento.component.css'
})
export class ContasReceberParcelamentoComponent implements OnInit, OnChanges, OnDestroy {
  private readonly CODIGO_DINHEIRO = Number(CodigoSefazEnum.Dinheiro);
  private readonly CODIGO_CARTAO_CREDITO = Number(CodigoSefazEnum.CartaoCredito);
  private readonly CODIGO_CARTAO_DEBITO = Number(CodigoSefazEnum.CartaoDebito);
  private readonly CODIGO_CREDIARIO = Number(CodigoSefazEnum.Crediario);
  private readonly CODIGO_BOLETO = Number(CodigoSefazEnum.Boleto);
  private readonly CODIGO_DEPOSITO = Number(CodigoSefazEnum.Deposito);
  private readonly CODIGO_PIX = Number(CodigoSefazEnum.Pix);

  private formaRecebimentoSubscription?: Subscription;
  private contaFinanceiraSubscription?: Subscription;

  @Input() parentForm!: FormGroup;
  @Input() isEditMode: boolean = false;
  @Input() qtdParcelas: number = 0;
  @Input() listaTiposParcelamento: TipoParcelamento[] = [];
  @Input() listaFrequencia: FrequenciaRecorrencia[] = [];
  @Input() listaContaFinanceira: ContaFinanceira[] = [];
  @Input() listaFormaRecebimento: FormaRecebimento[] = [];
  @Input() currencyMask: any;
  @Input() integerMask: any;
  @Input() hasParcelasEmAberto: boolean = false;
  @Input() idContasReceber: number | null = null;
  @Input() camposObrigatorios: CampoObrigatorioStruct[] = [];
  /** Documento gerado pelo pedido de venda: renegociar fica bloqueado (decisão da PO; a API também recusa). */
  @Input() isOrigemVenda: boolean = false;
  /** Documento gerado pela NF-e de saída: renegociar fica bloqueado como no pedido de venda (a API também recusa). */
  @Input() isOrigemNFeSaida: boolean = false;

  /** Por que renegociar está bloqueado; vazio quando o documento não veio de pedido de venda nem de NF-e de saída. */
  public get motivoRenegociarBloqueado(): string {
    if (this.isOrigemNFeSaida) return 'Documento gerado pela NF-e de saída: as parcelas só mudam pela nota.';
    if (this.isOrigemVenda) return 'Documento gerado pelo pedido de venda: as parcelas só mudam pelo pedido.';
    return '';
  }

  @Output() renegociarClick = new EventEmitter<void>();

  get isMultiParcela(): boolean {
    return this.isEditMode && this.qtdParcelas > 1;
  }

  public isSimulando: boolean = false;

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  get parcelamentoGroup(): FormGroup {
    return this.parentForm.get('parcelamento') as FormGroup;
  }

  get isRecorrente(): boolean {
    return this.parcelamentoGroup.get('isRecorrente')?.value;
  }

  /**
   * "Dia de Vencimento Fixo" só se aplica a recorrência mensal/anual. Em diária/semanal o
   * vencimento avança naturalmente, então o campo é ocultado.
   */
  get mostrarDiaVencimentoFixo(): boolean {
    const freq = this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.value;
    return freq === FrequenciaRecorrenciaEnum.Mes || freq === FrequenciaRecorrenciaEnum.Ano;
  }

  get diaVencimentoMask(): any {
    return { ...this.integerMask, max: 31 };
  }

  get parcelasArray(): FormArray {
    return this.parcelamentoGroup.get('parcelas') as FormArray;
  }

  // Estado do bloco recolhível de parcelas canceladas (fechado por padrão).
  public mostrarCanceladas: boolean = false;

  private isCancelada(ctrl: AbstractControl): boolean {
    return ctrl.get('idStatusContasReceber')?.value === StatusContasReceberEnum.Cancelada;
  }

  // Mantém o índice original no FormArray (necessário para o formGroupName) ao filtrar.
  get parcelasAtivas(): { ctrl: AbstractControl; index: number }[] {
    return this.parcelasArray.controls
      .map((ctrl, index) => ({ ctrl, index }))
      .filter(x => !this.isCancelada(x.ctrl));
  }

  get parcelasCanceladas(): { ctrl: AbstractControl; index: number }[] {
    return this.parcelasArray.controls
      .map((ctrl, index) => ({ ctrl, index }))
      .filter(x => this.isCancelada(x.ctrl));
  }

  get idFormaRecebimentoControl(): any { return this.parcelamentoGroup.get('idFormaRecebimento'); }
  get idContaFinanceiraControl(): any { return this.parcelamentoGroup.get('idContaFinanceira'); }

  get tipoParcelamentoSelecionado(): TipoParcelamento | null {
    const id = this.parcelamentoGroup.get('idTipoParcelamento')?.value;
    if (!id) return null;
    return this.listaTiposParcelamento.find(t => t.idTipoParcelamento === id) ?? null;
  }

  get isParcelado(): boolean {
    const tipo = this.tipoParcelamentoSelecionado;
    return tipo != null && tipo.quantidadeParcelas > 1;
  }

  get podeSimular(): boolean {
    return !this.isSimulando;
  }

  get listaContaFinanceiraFiltrada(): ContaFinanceira[] {
    const forma = this.getFormaRecebimentoSelecionada();
    if (!forma) {
      return this.listaContaFinanceira;
    }

    const codigoForma = this.parseCodigoForma(forma.codigoPagamentoSefaz);
    const idFormaRecebimento = this.getIdFormaRecebimento(forma);
    const isValeTroca = this.normalizarTexto(forma.descricao) === 'vale troca';

    if (
      codigoForma === this.CODIGO_DINHEIRO ||
      codigoForma === this.CODIGO_CREDIARIO ||
      isValeTroca
    ) {
      return this.listaContaFinanceira.filter(conta => conta.idTipoConta === TipoContaEnum.ContaCaixa);
    }

    if (codigoForma === this.CODIGO_DEPOSITO) {
      return this.listaContaFinanceira.filter(conta => conta.idTipoConta === TipoContaEnum.ContaBancaria);
    }

    // Pix tem dois meios, como o boleto: pelo banco (conta bancária) ou por intermediador (conta de recebimento
    // de um intermediador ativo com a forma). Espelho de ContaCompativelComFormaRecebimento (Finanças).
    if (codigoForma === this.CODIGO_PIX) {
      return this.listaContaFinanceira.filter(conta =>
        conta.idTipoConta === TipoContaEnum.ContaBancaria ||
        (idFormaRecebimento != null && this.isContaRecebimentoDeIntermediadorAtivo(conta, idFormaRecebimento))
      );
    }

    if (codigoForma === this.CODIGO_BOLETO) {
      const contasBancariasVinculadas = forma.contasBancariasVinculadas ?? [];
      return this.listaContaFinanceira.filter(conta => {
        const id = Number(conta.idContaFinanceira);
        return contasBancariasVinculadas.includes(id) ||
          this.isContaRecebimentoDeIntermediadorAtivo(conta, idFormaRecebimento);
      });
    }

    if (codigoForma === this.CODIGO_CARTAO_CREDITO || codigoForma === this.CODIGO_CARTAO_DEBITO) {
      const contasBancariasVinculadas = forma.contasBancariasVinculadas ?? [];
      return this.listaContaFinanceira.filter(conta => {
        const id = Number(conta.idContaFinanceira);
        return contasBancariasVinculadas.includes(id) ||
          this.isContaRecebimentoDeIntermediadorAtivo(conta, idFormaRecebimento);
      });
    }

    return this.listaContaFinanceira;
  }

  get listaFormaRecebimentoFiltrada(): FormaRecebimento[] {
    const conta = this.getContaFinanceiraSelecionada();
    if (!conta) {
      return this.listaFormaRecebimento;
    }

    if (conta.idTipoConta === TipoContaEnum.ContaCaixa) {
      return this.listaFormaRecebimento.filter(forma =>
        this.isFormaCaixa(this.parseCodigoForma(forma.codigoPagamentoSefaz), forma)
      );
    }

    if (conta.idTipoConta === TipoContaEnum.ContaBancaria) {
      const idConta = Number(conta.idContaFinanceira);
      return this.listaFormaRecebimento.filter(forma => {
        const codigoForma = this.parseCodigoForma(forma.codigoPagamentoSefaz);
        if (codigoForma === this.CODIGO_BOLETO) {
          const vinculadas = forma.contasBancariasVinculadas ?? [];
          return vinculadas.length === 0 || vinculadas.includes(idConta);
        }
        if (codigoForma === this.CODIGO_CARTAO_CREDITO || codigoForma === this.CODIGO_CARTAO_DEBITO) {
          const vinculadas = forma.contasBancariasVinculadas ?? [];
          return vinculadas.includes(idConta);
        }
        return codigoForma === this.CODIGO_DEPOSITO || codigoForma === this.CODIGO_PIX;
      });
    }

    if (conta.idTipoConta === TipoContaEnum.ContaRecebimento) {
      const idsFormasIntermediador = conta.idsFormasRecebimentoIntermediadorAtivo ?? [];
      return this.listaFormaRecebimento.filter(forma => {
        const codigoForma = this.parseCodigoForma(forma.codigoPagamentoSefaz);
        const idFormaRecebimento = this.getIdFormaRecebimento(forma);
        return idsFormasIntermediador.includes(idFormaRecebimento) &&
          (
            codigoForma === this.CODIGO_BOLETO ||
            codigoForma === this.CODIGO_CARTAO_CREDITO ||
            codigoForma === this.CODIGO_CARTAO_DEBITO
          );
      });
    }

    return this.listaFormaRecebimento;
  }

  constructor(
    private fb: FormBuilder,
    private contasReceberService: ContasReceberService,
    private alertService: AlertService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['listaContaFinanceira'] || changes['listaFormaRecebimento']) &&
      this.parentForm?.get('parcelamento')
    ) {
      this.limparContaFinanceiraInvalida();
    }
  }

  ngOnInit(): void {
    this.parcelamentoGroup.get('isRecorrente')?.valueChanges.subscribe(() => {
      this.parcelasArray.clear();
      this.parcelamentoGroup.patchValue({
        idTipoParcelamento: null,
        dataVencimento: null,
        intervaloDiasParcelas: null,
        recorrenciaIntervalo: null,
        idFrequenciaRecorrencia: null,
        recorrenciaDiaVencimento: null,
        recorrenciaQuantidade: null,
        isRecebido: false
      }, { emitEvent: false });
    });

    // Em frequência diária/semanal o "dia de vencimento fixo" não se aplica — limpa o valor.
    this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.valueChanges.subscribe(() => {
      if (!this.mostrarDiaVencimentoFixo) {
        this.parcelamentoGroup.get('recorrenciaDiaVencimento')?.setValue(null, { emitEvent: false });
      }
    });

    this.formaRecebimentoSubscription = this.idFormaRecebimentoControl?.valueChanges.subscribe(() => {
      this.limparContaFinanceiraInvalida();
    });
    this.contaFinanceiraSubscription = this.idContaFinanceiraControl?.valueChanges.subscribe(() => {
      this.limparFormaRecebimentoInvalida();
    });
    this.limparContaFinanceiraInvalida();
    this.limparFormaRecebimentoInvalida();
  }

  ngOnDestroy(): void {
    this.formaRecebimentoSubscription?.unsubscribe();
    this.contaFinanceiraSubscription?.unsubscribe();
  }

  public simular(): void {
    if (this.isRecorrente) {
      this.simularRecorrencia();
    } else {
      this.simularParcelas();
    }
  }

  private simularParcelas(): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    const idTipoParcelamento = this.parcelamentoGroup.get('idTipoParcelamento')?.value;
    const dataVencimento = this.parcelamentoGroup.get('dataVencimento')?.value;
    const intervaloDiasParcelas = this.parcelamentoGroup.get('intervaloDiasParcelas')?.value;
    const descricaoBase = this.parentForm.get('dadosGerais.descricao')?.value || '';

    if (!valorTotal || valorTotal <= 0) {
      this.alertService.show('Atenção', 'Preencha o valor total do documento em Dados Gerais.', AlertType.warning);
      return;
    }
    if (!idTipoParcelamento) {
      this.alertService.show('Atenção', 'Selecione o tipo de parcelamento.', AlertType.warning);
      return;
    }
    if (!dataVencimento) {
      this.alertService.show('Atenção', 'Informe a data de vencimento inicial.', AlertType.warning);
      return;
    }

    const request = {
      valorTotal,
      idTipoParcelamento,
      dataVencimento: new Date(dataVencimento).toISOString(),
      intervaloDiasParcelas: intervaloDiasParcelas || 30,
      descricaoBase
    };

    this.isSimulando = true;
    this.contasReceberService.simularParcelas(request).subscribe({
      next: (res: any) => {
        this.isSimulando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao simular parcelas.', AlertType.error);
          return;
        }
        this.popularGridParcelas(res.parcelas || []);
      },
      error: () => {
        this.isSimulando = false;
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      }
    });
  }

  private simularRecorrencia(): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    const intervalo = this.parcelamentoGroup.get('recorrenciaIntervalo')?.value || 1;
    const idFrequenciaRecorrencia = this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.value;
    const diaVencimentoFixo = this.parcelamentoGroup.get('recorrenciaDiaVencimento')?.value || 0;
    const quantidadeVezes = this.parcelamentoGroup.get('recorrenciaQuantidade')?.value;
    const descricaoBase = this.parentForm.get('dadosGerais.descricao')?.value || '';
    const dataInicio = this.parcelamentoGroup.get('dataVencimento')?.value;

    if (!valorTotal || valorTotal <= 0) {
      this.alertService.show('Atenção', 'Preencha o valor total do documento em Dados Gerais.', AlertType.warning);
      return;
    }
    if (!quantidadeVezes || quantidadeVezes < 1) {
      this.alertService.show('Atenção', 'Informe a quantidade de vezes.', AlertType.warning);
      return;
    }
    if (!idFrequenciaRecorrencia) {
      this.alertService.show('Atenção', 'Selecione o período da recorrência.', AlertType.warning);
      return;
    }
    if (!dataInicio) {
      this.alertService.show('Atenção', 'Informe a data de vencimento do 1º lançamento.', AlertType.warning);
      return;
    }

    const request = {
      valorTotal,
      intervalo,
      idFrequenciaRecorrencia,
      diaVencimentoFixo,
      quantidadeVezes,
      descricaoBase,
      dataInicio: new Date(dataInicio).toISOString()
    };

    this.isSimulando = true;
    this.contasReceberService.simularRecorrencia(request).subscribe({
      next: (res: any) => {
        this.isSimulando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao simular recorrência.', AlertType.error);
          return;
        }
        this.popularGridRecorrencia(res.parcelas || []);
      },
      error: () => {
        this.isSimulando = false;
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      }
    });
  }

  private popularGridParcelas(parcelas: any[]): void {
    this.parcelasArray.clear();
    parcelas.forEach((p: any) => {
      this.parcelasArray.push(this.fb.group({
        nroParcela: [p.nroParcela],
        dataVencimento: [p.dataVencimento ? new Date(p.dataVencimento) : null],
        valor: [p.valor],
        percentual: [p.percentual],
        descricao: [p.descricao]
      }));
    });
  }

  private popularGridRecorrencia(parcelas: any[]): void {
    this.parcelasArray.clear();
    parcelas.forEach((p: any) => {
      this.parcelasArray.push(this.fb.group({
        nroOcorrencia: [p.nroOcorrencia],
        dataCriacao: [p.dataCriacao ? new Date(p.dataCriacao) : null],
        dataVencimento: [p.dataVencimento ? new Date(p.dataVencimento) : null],
        descricao: [p.descricao],
        valor: [p.valor]
      }));
    });
  }

  public onParcelaValorChange(index: number): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    this.atualizarPercentual(index, valorTotal);
    this.fecharSomaNaUltima(index, valorTotal);
  }

  /**
   * A soma das parcelas é sempre idêntica ao total do documento: a diferença de uma parcela editada vai para a
   * última (se a editada for a última, para a anterior), como a venda faz. Com uma parcela só, não há onde pôr.
   */
  private fecharSomaNaUltima(indexEditado: number, valorTotal: number): void {
    const total = UtilService.arredondarAbnt(valorTotal, 2);
    const n = this.parcelasArray.length;
    if (n < 2 || total <= 0) return;

    const alvo = indexEditado === n - 1 ? n - 2 : n - 1;
    const somaOutras = this.parcelasArray.controls.reduce((soma, ctrl, i) => soma + (i === alvo ? 0 : (ctrl.get('valor')?.value || 0)), 0);
    const valorAlvo = UtilService.arredondarAbnt(total - somaOutras, 2);
    if (valorAlvo < 0) return;

    this.parcelasArray.at(alvo).get('valor')?.setValue(valorAlvo, { emitEvent: false });
    this.atualizarPercentual(alvo, valorTotal);
  }

  private atualizarPercentual(index: number, valorTotal: number): void {
    const ctrl = this.parcelasArray.at(index);
    const valor = ctrl.get('valor')?.value || 0;
    const perc = valorTotal > 0 ? Math.round((valor / valorTotal) * 10000) / 100 : 0;
    ctrl.get('percentual')?.setValue(perc, { emitEvent: false });
  }

  public get somaParcelas(): number {
    if (this.isRecorrente) return 0;
    return this.parcelasArray.controls.reduce((soma, ctrl) => soma + (ctrl.get('valor')?.value || 0), 0);
  }

  public get somaParcelasValida(): boolean {
    if (this.isRecorrente || this.parcelasArray.length === 0) return true;
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    return UtilService.arredondarAbnt(this.somaParcelas, 2) === UtilService.arredondarAbnt(valorTotal, 2);
  }


  onDateInput(event: Event, controlName: string, index?: number): void {
    const control = index !== undefined
      ? this.parcelasArray.at(index)?.get(controlName)
      : this.parcelamentoGroup.get(controlName);
    MaskService.onDateInput(event, control);
  }

  private getFormaRecebimentoSelecionada(): FormaRecebimento | null {
    const idFormaRecebimento = this.getIdFormaRecebimentoSelecionada();
    if (!idFormaRecebimento) {
      return null;
    }

    return this.listaFormaRecebimento.find(forma => this.getIdFormaRecebimento(forma) === idFormaRecebimento) ?? null;
  }

  private getContaFinanceiraSelecionada(): ContaFinanceira | null {
    const value = this.idContaFinanceiraControl?.value;
    const idContaFinanceira = typeof value === 'object' && value
      ? Number((value as ContaFinanceira).idContaFinanceira)
      : Number(value);

    if (!Number.isFinite(idContaFinanceira) || idContaFinanceira <= 0) {
      return null;
    }

    return this.listaContaFinanceira.find(conta =>
      Number(conta.idContaFinanceira) === idContaFinanceira
    ) ?? null;
  }

  private getIdFormaRecebimentoSelecionada(): number | null {
    const value = this.idFormaRecebimentoControl?.value;
    const id = typeof value === 'object' && value
      ? this.getIdFormaRecebimento(value as FormaRecebimento)
      : Number(value);

    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private getIdFormaRecebimento(forma: FormaRecebimento): number {
    return Number(forma.idFormaRecebimento ?? forma.id);
  }

  private parseCodigoForma(codigo: number | string | null | undefined): number | null {
    if (codigo === null || codigo === undefined || codigo === '') {
      return null;
    }

    const value = Number(codigo);
    return Number.isFinite(value) ? value : null;
  }

  private isContaRecebimentoDeIntermediadorAtivo(conta: ContaFinanceira, idFormaRecebimento: number): boolean {
    return conta.idTipoConta === TipoContaEnum.ContaRecebimento &&
      (conta.idsFormasRecebimentoIntermediadorAtivo ?? []).includes(idFormaRecebimento);
  }

  private isFormaCaixa(codigoForma: number | null, forma: FormaRecebimento): boolean {
    return codigoForma === this.CODIGO_DINHEIRO ||
      codigoForma === this.CODIGO_CREDIARIO ||
      this.normalizarTexto(forma.descricao) === 'vale troca';
  }

  private limparContaFinanceiraInvalida(): void {
    const idContaFinanceira = this.idContaFinanceiraControl?.value;
    if (!idContaFinanceira) {
      return;
    }

    const contaSelecionadaValida = this.listaContaFinanceiraFiltrada.some(conta =>
      Number(conta.idContaFinanceira) === Number(idContaFinanceira)
    );

    if (!contaSelecionadaValida) {
      this.idContaFinanceiraControl?.setValue(null, { emitEvent: false });
    }
  }

  private limparFormaRecebimentoInvalida(): void {
    const idFormaRecebimento = this.getIdFormaRecebimentoSelecionada();
    if (!idFormaRecebimento) {
      return;
    }

    const formaSelecionadaValida = this.listaFormaRecebimentoFiltrada.some(forma =>
      this.getIdFormaRecebimento(forma) === idFormaRecebimento
    );

    if (!formaSelecionadaValida) {
      this.idFormaRecebimentoControl?.setValue(null, { emitEvent: false });
    }
  }

  private normalizarTexto(value: string | null | undefined): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/-/g, ' ')
      .trim()
      .toLowerCase();
  }
}
